import { Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel, Typography } from '@material-ui/core';
import { selectStores } from 'app/main/apps/stores/ducks/stores.selector';
import { getStoreTags } from 'app/main/apps/stores/ducks/stores.thunk';
import { StoreDetails } from 'app/main/apps/stores/type';
import { notificationWebPushDialogSelector } from 'app/main/apps/webPush/ducks/webPush.selector';
import {
  setNotificationWebPushDialog,
  updateWebPushSubscriptionStores,
  updateWebPushSubscriptionTags,
} from 'app/main/apps/webPush/ducks/webPush.slice';
import {
  unsubscribeFromWebPushNotificationsThunk,
  updateWebPushSubscriptionStoresThunk,
} from 'app/main/apps/webPush/ducks/webPush.thunk';
import { SelectedStoreDTO, WebPushSubscriptionInDTO } from 'app/main/apps/webPush/type';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

interface NotificationSettingsContentProps {
  subscription: WebPushSubscriptionInDTO;
}

const NotificationSettings: React.FC<NotificationSettingsContentProps> = ({ subscription }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLoading, hasError } = useSelector(notificationWebPushDialogSelector);
  const stores = useSelector(selectStores);

  const isStoreSelected = (storeId: string) => subscription.selectedStores.some(s => s.storeId === storeId);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation_todo>
  useEffect(() => {
    subscription.selectedStores.forEach(store => {
      const storeDetails = stores.find(s => s.id === store.storeId);
      if (storeDetails && !storeDetails.tags) {
        dispatch(
          getStoreTags({
            franchiseId: storeDetails.franchiseId,
            storeId: storeDetails.id,
            includeDisabled: true,
          }),
        );
      }
    });
  }, [stores, subscription.selectedStores]);

  const isTagSelected = (storeId: string, tagId: string) => {
    const store = subscription.selectedStores.find(s => s.storeId === storeId);
    return store ? store.tagIds.includes(tagId) : false;
  };

  const handleStoreSelection = (store: StoreDetails) => {
    let updatedStores: SelectedStoreDTO[];
    if (!isStoreSelected(store.id)) {
      updatedStores = [
        ...subscription.selectedStores,
        {
          storeId: store.id,
          types: [],
          tagIds: [],
        },
      ];
    } else {
      updatedStores = subscription.selectedStores.filter(s => s.storeId !== store.id);
    }
    dispatch(updateWebPushSubscriptionStores(updatedStores));
  };

  const handleTagSelection = (storeId: string, tagId: string) => {
    const store = subscription.selectedStores.find(s => s.storeId === storeId);
    let updatedTagIds: string[] = [];
    if (!isTagSelected(storeId, tagId)) {
      updatedTagIds = [...(store?.tagIds || []), tagId];
    } else {
      updatedTagIds = updatedTagIds.filter(id => id !== tagId);
    }
    dispatch(updateWebPushSubscriptionTags({ storeId, tagIds: updatedTagIds }));
  };

  return (
    <div className="m-0 px-0 py-24 px-24 flex flex-col items-center">
      {hasError ? (
        <div className="flex justify-center items-center h-512 w-512">{t('basic.errors.load')}</div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-512 w-512">
          <CircularProgress />
        </div>
      ) : (
        <>
          <Card elevation={0} className="border-1 border-solid border-gray-300 rounded-lg w-full mb-16 flex-shrink-0">
            <CardContent className="flex flex-col">
              <Typography variant="h6" color="textPrimary">
                {t('notification.dialog.deactivateTitle', { ns: 'notification' })}
              </Typography>
              <Typography variant="body1" className="mt-8 mb-16 text-gray-700">
                {t('notification.dialog.deactivateDescription', { ns: 'notification' })}
              </Typography>
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    dispatch(unsubscribeFromWebPushNotificationsThunk(subscription.id));
                    dispatch(setNotificationWebPushDialog({ isOpen: false }));
                  }}
                  className="bg-red-500 hover:bg-red-700 text-white normal-case"
                  variant="contained"
                >
                  {t('notification.dialog.deactivate', { ns: 'notification' }).toUpperCase()}
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card elevation={0} className="border-1 border-solid border-gray-300 rounded-lg w-full overflow-auto ">
            <CardContent className="flex flex-col">
              <Typography variant="h6" color="textPrimary">
                {t('notification.dialog.settingsTitle', { ns: 'notification' })}
              </Typography>
              <Typography variant="body1" className="mt-4 mb-16 text-gray-700">
                {t('notification.dialog.settingsDescription', { ns: 'notification' })}
              </Typography>
              <div className="flex flex-col h-full overflow-auto">
                {stores.map(store => (
                  <div key={store.id}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={isStoreSelected(store.id)} onChange={() => handleStoreSelection(store)} />
                      }
                      label={store.name}
                    />

                    {isStoreSelected(store.id) && store.tags && store.tags.data.length > 0 && (
                      <Card elevation={0} className="ml-24 border-1 border-solid border-gray-200 rounded-lg mb-16">
                        <CardContent className="flex flex-col p-8">
                          <Typography variant="body1" className="mt-8 text-gray-700">
                            {t('notification.dialog.settingsTags', { ns: 'notification' })}
                          </Typography>
                          {store.tags.data.map(tag => (
                            <FormControlLabel
                              key={tag.tagId}
                              control={
                                <Checkbox
                                  checked={isTagSelected(store.id, tag.tagId)}
                                  onChange={() => handleTagSelection(store.id, tag.tagId)}
                                  name="checkedG"
                                />
                              }
                              label={t('notification.dialog.tag', {
                                ns: 'notification',
                                tagName: tag.name ?? tag.tagId,
                              })}
                            />
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-between items-center gap-16 w-full mt-24">
            <Button
              onClick={() => dispatch(setNotificationWebPushDialog({ isOpen: false }))}
              className="normal-case"
              variant="contained"
              color="default"
            >
              {t('notification.dialog.cancel', { ns: 'notification' }).toUpperCase()}
            </Button>
            <Button
              onClick={() => {
                dispatch(updateWebPushSubscriptionStoresThunk());
              }}
              className="normal-case"
              variant="contained"
              color="primary"
            >
              {t('notification.dialog.validate', { ns: 'notification' }).toUpperCase()}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSettings;
